import React, { useCallback, useEffect, useState, useRef } from 'react'
import dynamic from 'next/dynamic'
import { AppProps } from 'next/app'
import { useRouter } from 'next/router'
import { ThemeProvider } from 'styled-components'
import {
  getXCLEARCorrelationInfo,
  getCLEARCorrelationInfo,
  getAccessToken,
} from '@api/utils'
import {
  useEnrollmentContext,
  EnrollmentProvider,
} from '@contexts/EnrollmentContext'
import { Enrollment as EnrollmentClient } from '@clear-denver/tpc-openapi-typescript'

import '@clear/design-system/assets/fonts/fonts.css'
import { GlobalStyle } from '@clear/design-system'

import Layout from '@components/shared/Layout'
import '../src/styles/tailwind.css'
import '../src/styles/locations.css'
import '../src/styles/otp-input.css'
import theme from 'src/styles/theme'
import { RouteGuard } from '@components/RouteGuard'
import axios from 'axios'
import { LocationsProvider } from '@contexts/LocationsContext'
import { ConfigProvider, useConfigContext } from '@contexts/ConfigContext'
import { PricingConfigProvider } from '@contexts/PricingContext'
import { FocusTaskProvider } from '@contexts/FocusTaskContext'
import { useFunnelTransition } from '@hooks/useFunnelTransition'

const App = ({ Component, pageProps }: AppProps) => {
  useFunnelTransition()

  const Gtm = dynamic(() => import('@components/Gtm'), {
    ssr: false,
  })

  const router = useRouter()
  const mainScriptRef = useRef<HTMLScriptElement | null>(null)
  const observerRef = useRef<MutationObserver | null>(null)
  const childScriptRef = useRef<HTMLScriptElement | null>(null)

  const WHITELISTED_PAGES = [
    '/',
    '/help',
    '/locations',
    '/terms',
    '/about',
    '/important-notices',
  ]

  const cleanupScripts = () => {
    if (mainScriptRef.current?.parentElement) {
      mainScriptRef.current.parentElement.removeChild(mainScriptRef.current)
      mainScriptRef.current = null
    }
    if (childScriptRef.current?.parentElement) {
      childScriptRef.current.parentElement.removeChild(childScriptRef.current)
      childScriptRef.current = null
    }
    const existingChildScript = document.querySelector(
      'script[src="https://dap.digitalgov.gov/web-vitals/dist/web-vitals.attribution.iife.js"]'
    ) as HTMLScriptElement
    if (existingChildScript?.parentElement) {
      existingChildScript.parentElement.removeChild(existingChildScript)
    }
    if (observerRef.current) {
      observerRef.current.disconnect()
      observerRef.current = null
    }
  }
  const startWatchingForChildScript = () => {
    observerRef.current = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeName === 'SCRIPT') {
            const script = node as HTMLScriptElement
            if (script === mainScriptRef.current) {
              return
            }
            if (
              script.src ===
              'https://dap.digitalgov.gov/web-vitals/dist/web-vitals.attribution.iife.js'
            ) {
              childScriptRef.current = script
            }
          }
        })
      })
    })
    observerRef.current.observe(document.documentElement, {
      childList: true,
      subtree: true,
    })
  }
  const addAnalyticsScript = () => {
    cleanupScripts()
    startWatchingForChildScript()
    const script = document.createElement('script')
    script.id = '_fed_an_ua_tag'
    script.src =
      'https://dap.digitalgov.gov/Universal-Federated-Analytics-Min.js?agency=DHS'
    script.async = true
    mainScriptRef.current = script
    document.head.appendChild(script)
  }
  useEffect(() => {
    if (!router.isReady) return
    const currentPath = router.pathname
    const isWhitelisted = WHITELISTED_PAGES.includes(currentPath)
    if (isWhitelisted) {
      addAnalyticsScript()
    } else {
      cleanupScripts()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router.isReady, router.pathname])

  useEffect(() => {
    const handleRouteStart = (url: string) => {
      const path = url.split('?')[0].split('#')[0]
      const isWhitelisted = WHITELISTED_PAGES.includes(path)
      if (!isWhitelisted) {
        cleanupScripts()
      }
    }
    const handleRouteComplete = (url: string) => {
      const path = url.split('?')[0].split('#')[0]
      const isWhitelisted = WHITELISTED_PAGES.includes(path)
      if (isWhitelisted && !mainScriptRef.current) {
        addAnalyticsScript()
      } else if (!isWhitelisted) {
        cleanupScripts()
      }
    }
    router.events.on('routeChangeStart', handleRouteStart)
    router.events.on('routeChangeComplete', handleRouteComplete)
    return () => {
      router.events.off('routeChangeStart', handleRouteStart)
      router.events.off('routeChangeComplete', handleRouteComplete)
      cleanupScripts()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router])

  if (process.browser) {
    router.events.on('routeChangeComplete', () => {
      window.scrollTo(0, 0)
    })
  }

  const AppContent = ({ Component, pageProps }: AppProps) => {
    const [errorSent, setErrorSent] = useState(false)
    const [infoSent, setInfoSent] = useState(false)
    const { state } = useEnrollmentContext()
    const router = useRouter()
    const { sessionId, requestId } = getCLEARCorrelationInfo()
    const { config } = useConfigContext()

    const getFirstClassContext = useCallback(
      () => ({
        config,
        sessionId,
        requestId,
        enrollmentId: state.id,
        userAgent:
          typeof window !== 'undefined'
            ? window?.navigator.userAgent
            : 'server-or-test',
        url: router.route,
      }),
      [config, requestId, router.route, sessionId, state.id]
    )

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const postLoggingEndpoint = async (arg: any) => {
      try {
        await axios.post(
          `${process.env.NEXT_PUBLIC_ENROLLMENT_API_ENDPOINT}/public/reporting/logging`,
          arg,
          {
            headers: {
              'Content-Type': 'application/json',
              'X-Clear-Correlationinfo': getXCLEARCorrelationInfo(),
              Authorization: getAccessToken(),
            },
          }
        )
      } catch (error) {
        console.error(error)
      }
    }

    const shipErrorToApi = useCallback(
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (evt: any) => {
        const { payload = {}, metadata = {} } = evt?.detail
        if (errorSent) {
          setErrorSent(false)
        } else {
          const evtError = {
            createdTime: new Date(),
            level: EnrollmentClient.LogEntryLevelEnum.ERROR,
            enrollmentId: state.id,
            payload,
            metadata: {
              ...getFirstClassContext(),
              ...metadata,
            },
          }
          try {
            setErrorSent(true)
            postLoggingEndpoint(evtError)
            if (process.env.NEXT_PUBLIC_APP_ENV === 'development') {
              console.error(evtError)
            }
          } catch (e) {
            console.log('Unable to log error.', e)
            console.log(evt)
          }
        }
        return
      },
      [errorSent, state.id, getFirstClassContext]
    )

    const shipInfoToApi = useCallback(
      async ({ detail }) => {
        const { payload = {}, metadata = {} } = detail
        if (infoSent) {
          setInfoSent(false)
        } else {
          const evtInfo = {
            createdTime: new Date(),
            level: EnrollmentClient.LogEntryLevelEnum.INFO,
            enrollmentId: state.id,
            payload,
            metadata: {
              ...getFirstClassContext(),
              ...metadata,
            },
          }
          try {
            setInfoSent(true)
            postLoggingEndpoint(evtInfo)
            if (process.env.NEXT_PUBLIC_APP_ENV === 'development') {
              console.info(evtInfo)
            }
          } catch (e) {
            console.log('Unable to send info log.', e)
          }
        }
        return
      },
      [infoSent, state.id, getFirstClassContext]
    )

    useEffect(() => {
      const listener = (data) => {
        shipInfoToApi(data)
      }
      document.addEventListener(
        EnrollmentClient.LogEntryLevelEnum.INFO,
        listener
      )
      return () =>
        document.removeEventListener(
          EnrollmentClient.LogEntryLevelEnum.INFO,
          listener
        )
    }, [shipInfoToApi])

    useEffect(() => {
      const listener = (event: CustomEvent) => {
        shipErrorToApi(event)
      }
      document.addEventListener(
        EnrollmentClient.LogEntryLevelEnum.ERROR,
        listener
      )
      return () => {
        document.removeEventListener(
          EnrollmentClient.LogEntryLevelEnum.ERROR,
          listener
        )
      }
    }, [shipErrorToApi])

    return <Component {...pageProps} />
  }

  return (
    <>
      <ThemeProvider theme={theme}>
        <ConfigProvider>
          <PricingConfigProvider>
            <EnrollmentProvider>
              <LocationsProvider>
                <Layout>
                  <FocusTaskProvider>
                    <RouteGuard>
                      <Gtm />
                      <AppContent Component={Component} {...pageProps} />
                    </RouteGuard>
                  </FocusTaskProvider>
                </Layout>
              </LocationsProvider>
            </EnrollmentProvider>
          </PricingConfigProvider>
        </ConfigProvider>
        <GlobalStyle />
      </ThemeProvider>
    </>
  )
}
export default App
